import BaseTabs from 'components/atoms/base-tabs';
import { useTranslation } from 'react-i18next';
import { useRpmPatientCharts } from 'infrastructure/hooks/rpm';
import Charts from 'components/templates/rpm/patients/patient/charts';
import { useState, type FC } from 'react';
import React from 'react';
import { makeid } from 'utils/helpers/makeid';
import classNames from 'classnames';
import ConditionGoals from 'components/molecules/conditions-goals';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import ServicesIcon from 'components/atoms/icons/services';
import ExpandButton from 'components/molecules/expand-button';
import { useLocalStorage } from 'infrastructure/hooks/utils/use-local-storage';
import CollapseAnimation from 'components/atoms/base-animations/collapse';
import { useModule } from 'infrastructure/providers/module-provider';
import { ModuleType } from 'infrastructure/enums';

import s from './styles.module.scss';
import PatientAppointmentDetails from '../appointment-details';
import PatientAllergyLog from '../allergy-log';
import PatientSelfGoals from '../patient-self-goals';
import PatientHealthQuestionnaire from '../patient-health-questionnaire';

import type { IPatientThresholds } from 'infrastructure/interfaces';
import type { Nullable } from 'infrastructure/types';
import type { IBaseTab } from 'components/atoms/base-tabs';

const getInnerContentMap = (
  patientGuid: string,
  disableActions: boolean,
): { [key: number]: React.ReactNode } => ({
  0: (
    <PatientAppointmentDetails
      patientGuid={patientGuid}
      disableActions={disableActions}
    />
  ),
  1: (
    <PatientAllergyLog
      patientGuid={patientGuid}
      disableActions={disableActions}
    />
  ),
  2: (
    <PatientSelfGoals
      patientGuid={patientGuid}
      withoutCondition
      disableActions={disableActions}
    />
  ),
  3: (
    <PatientHealthQuestionnaire
      patientGuid={patientGuid}
      disableActions={disableActions}
    />
  ),
  4: (
    <ConditionGoals
      patientGuid={patientGuid}
      isSingle
      disableActions={disableActions}
    />
  ),
});

interface ServicesProps {
  patientGuid: string;
  patientDetailsLoading: boolean;
  thresholds?: Nullable<IPatientThresholds>;
}

const Services: FC<ServicesProps> = ({
  patientGuid,
  patientDetailsLoading,
  thresholds,
}) => {
  const { module: tabModule, changeModule: setTabModule } = useModule();

  const [innerTab, setInnerTab] = useState(0);
  const { t } = useTranslation();
  const { charts, isLoading: isChartsLoading } = useRpmPatientCharts({
    patientGuid,
  });
  const { isReadOnlyRole } = useCheckRoleAccess();

  const { value, setValue } = useLocalStorage(
    `services-table-opened-state`,
    true,
  );

  const internalTabs: IBaseTab[] = [
    {
      key: makeid(5),
      label: t('labels.patientAppointment'),
    },
    {
      key: makeid(5),
      label: t('labels.allergyLog'),
    },
    {
      key: makeid(5),
      label: t('labels.patientGoals'),
    },
    {
      key: makeid(5),
      label: t('labels.questionnaire'),
    },
    {
      key: makeid(5),
      label: t('labels.conditions'),
    },
  ];

  const mainTabs: IBaseTab<ModuleType>[] = [
    {
      key: ModuleType.RPM,
      label: t('labels.realTimeMonitoringRpm'),
      children: (
        <div className={s['graph-wrapper']}>
          <h3 className={s['graph-title']}>{t('labels.readingsGraph')}</h3>
          <div className={s.divider} />
          <Charts
            charts={charts}
            patientDetailsLoading={patientDetailsLoading}
            chartsLoading={isChartsLoading}
            thresholds={thresholds}
            className={s['graph-container']}
          />
        </div>
      ),
    },
    {
      key: ModuleType.CCM,
      label: t('labels.chronicCareManagementCcm'),
      children: (
        <div className={s['chronic-wrapper']}>
          <div className={s['chronic-header']}>
            <BaseTabs
              tabs={internalTabs}
              defaultIndex={innerTab}
              onChange={(_, index) => setInnerTab(index)}
              key={makeid(5)}
              type="rounded"
              size="small"
            />
          </div>
          <div className={s['chronic-body']}>
            {getInnerContentMap(patientGuid, isReadOnlyRole)[innerTab]}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className={s.wrapper} id="services">
      <div className={s.header}>
        <h4 className={s.title}>
          <ServicesIcon />
          {t('labels.services')}
        </h4>
        <ExpandButton
          expanded={value!}
          dataCy="toggle-services-btn"
          onClick={() => setValue(!value)}
        />
      </div>
      <CollapseAnimation opened={value!} className={s.collapse}>
        <div className={s.divider} />
        <BaseTabs
          tabs={mainTabs}
          key={
            tabModule === ModuleType.CCM
              ? `ccm-${internalTabs[innerTab].label}`
              : `rpm-${makeid(5)}`
          }
          activeKey={tabModule}
          onChange={(tab) => setTabModule(tab.key as ModuleType)}
          contentClassName={classNames(s.content, {
            [s['main-tab-content']]: tabModule === ModuleType.CCM,
          })}
        />
      </CollapseAnimation>
    </div>
  );
};
export default Services;
